import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useAppState } from "@/state/AppStateProvider";
import { Sparkles } from "lucide-react";
import { DISCOVERY_OPTIONS, HASHTAG_OPTIONS, normalizeRecipeDiscovery } from "@/lib/discoveryOptions";

/**
 * The brand "recipe" for a generation. Two parts:
 *   1. Foundation — Brand → Voice + (one) Writing style (the post's *format*).
 *      Industry knowledge + Critique are automatic (driven by the brand).
 *   2. Layers (optional, compose on top of the format):
 *      Goal (sell/educate/entertain) + CTA toggle (+ type) + Discovery + Hashtags.
 * Controlled component:
 *   value = { brandId, voiceId, styleSkillId, goal, cta, ctaType, discoveryMode, hashtagMode }
 *   onChange(next) with the same shape.
 */
const GOALS = [
  { id: "", label: "Let the voice decide", hint: "No explicit intent layer." },
  { id: "sell", label: "Sell — drive action", hint: "Lead toward the offer/outcome. Persuasive, never pushy." },
  { id: "educate", label: "Educate — build trust", hint: "Inform clearly, establish credibility. No hard sell." },
  { id: "entertain", label: "Entertain — build affinity", hint: "Human, warm, genuine. A relationship post, not a pitch." },
];
export function RecipePicker({ value = {}, onChange, variant = "light", context = "social" }) {
  const { state } = useAppState();
  const brands = state.brands || [];
  const isBlog = context === "blog";
  const styles = useMemo(
    () => (state.skills || []).filter((s) => {
      if (!s.enabled || s.id === "sk_instagram_post") return false;
      // Formats are post SHAPES only. Industry-knowledge + critique skills are
      // auto-applied, never user-picked here — exclude them by category AND by id
      // (a stale localStorage cache from before `category` existed can omit it,
      // which previously let SalonSpaKnowledge etc. leak into this dropdown).
      if (s.category === "industry" || s.category === "critique") return false;
      if (s.id?.startsWith("sk_industry_") || s.id === "sk_critique" || s.id === "sk_compliance_check") return false;
      // Blog formats (sk_blog_*) are long-form shapes — show them ONLY in the blog
      // recipe; the social shapes show everywhere else.
      const isBlogFormat = s.id?.startsWith("sk_blog_");
      if (isBlog ? !isBlogFormat : isBlogFormat) return false;
      return s.category === "style" || (!s.category && s.phase === "generation");
    }),
    [state.skills, isBlog],
  );

  const brand =
    brands.find((b) => b.id === value.brandId) ||
    brands.find((b) => b.id === state.activeBrandId) ||
    brands[0] || null;
  const voices = brand?.voices || [];
  const primaryVoiceId = (voices.find((v) => v.is_primary) || voices[0])?.id || null;
  const selectedVoice = voices.find((v) => v.id === (value.voiceId || primaryVoiceId)) || null;
  const selectedStyle = styles.find((s) => s.id === value.styleSkillId) || null;
  const normalizedValue = normalizeRecipeDiscovery(value);

  // Self-default: make sure the controlled value always points at a real
  // brand + that brand's primary voice, without the parent having to.
  useEffect(() => {
    if (!brand) return;
    const validVoice = voices.some((v) => v.id === value.voiceId);
    const next = normalizeRecipeDiscovery({
      ...normalizedValue,
      brandId: brand.id,
      voiceId: validVoice ? value.voiceId : primaryVoiceId,
      styleSkillId: value.styleSkillId || "",
    });
    if (
      next.brandId !== value.brandId ||
      next.voiceId !== value.voiceId ||
      next.styleSkillId !== value.styleSkillId ||
      next.discoveryMode !== value.discoveryMode ||
      next.hashtagMode !== value.hashtagMode
    ) {
      onChange?.(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.id, primaryVoiceId, voices.length]);

  const set = (patch) => onChange?.(normalizeRecipeDiscovery({ ...value, brandId: brand?.id, voiceId: value.voiceId, styleSkillId: value.styleSkillId || "", ...patch }));

  const goal = normalizedValue.goal || "";
  const ctaOn = normalizedValue.cta === true;
  const discoveryMode = normalizedValue.discoveryMode;
  const hashtagMode = normalizedValue.hashtagMode;
  const discoveryOption = DISCOVERY_OPTIONS.find((option) => option.id === discoveryMode) || DISCOVERY_OPTIONS[0];
  const hashtagOption = HASHTAG_OPTIONS.find((option) => option.id === hashtagMode) || HASHTAG_OPTIONS[0];

  const triggerCls =
    variant === "dark"
      ? "border-white/15 bg-white/10 text-slate-100"
      : "bg-[color:var(--surface-2)]";
  const triggerStyle = variant === "dark" ? undefined : { borderColor: "var(--border-subtle)" };

  if (brands.length === 0) {
    return (
      <p className="text-[12.5px] text-muted-foreground">
        No brands yet. <Link to="/brands" className="text-[color:var(--accent-cyan)] hover:underline">Create a brand</Link> to set its voice.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="recipe-picker">
      <div className="space-y-1">
        <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Brand</label>
        <Select value={brand?.id || ""} onValueChange={(v) => set({ brandId: v, voiceId: undefined })}>
          <SelectTrigger className={triggerCls} style={triggerStyle} data-testid="recipe-brand"><SelectValue /></SelectTrigger>
          <SelectContent>
            {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name || "Untitled brand"}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Voice</label>
        <Select value={value.voiceId || primaryVoiceId || ""} onValueChange={(v) => set({ voiceId: v })}>
          <SelectTrigger className={triggerCls} style={triggerStyle} data-testid="recipe-voice"><SelectValue placeholder="Pick a voice" /></SelectTrigger>
          <SelectContent>
            {voices.length === 0
              ? <SelectItem value="none" disabled>No voices — add one in Brands</SelectItem>
              : voices.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.is_primary ? " (primary)" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Format</label>
        <Select value={value.styleSkillId || "auto"} onValueChange={(v) => set({ styleSkillId: v === "auto" ? "" : v })}>
          <SelectTrigger className={triggerCls} style={triggerStyle} data-testid="recipe-style"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Let the agent decide</SelectItem>
            {styles.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                <span className="font-medium">{s.name}</span>
                {s.description ? <span className="ml-2 text-muted-foreground">— {s.description}</span> : null}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] leading-snug text-muted-foreground">The post's shape — pick one. Intent (sell/educate) is set below, not here.</p>
      </div>

      {/* Layers — optional intent + add-ons that compose on top of the format. */}
      <div className="sm:col-span-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 rounded-lg border p-3" style={{ borderColor: variant === "dark" ? "rgba(255,255,255,0.12)" : "var(--border-subtle)", backgroundColor: variant === "dark" ? "rgba(255,255,255,0.04)" : "var(--surface-1)" }} data-testid="recipe-layers">
        <div className="space-y-1 sm:col-span-2 lg:col-span-4">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Layers (optional)</span>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Goal</label>
          <Select value={goal || "none"} onValueChange={(v) => set({ goal: v === "none" ? "" : v })}>
            <SelectTrigger className={triggerCls} style={triggerStyle} data-testid="recipe-goal"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GOALS.map((g) => (
                <SelectItem key={g.id || "none"} value={g.id || "none"}>{g.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Call to action</label>
            <Switch checked={ctaOn} onCheckedChange={(c) => set({ cta: c ? true : false })} data-testid="recipe-cta" />
          </div>
          {ctaOn ? (
            <Input
              value={value.ctaType || ""}
              onChange={(e) => set({ ctaType: e.target.value })}
              placeholder="e.g. book a call, DM me, link in bio"
              className={triggerCls}
              style={triggerStyle}
              data-testid="recipe-cta-type"
            />
          ) : (
            <p className="text-[11px] text-muted-foreground">{value.cta === false ? "No CTA — won't ask the reader to buy or book." : "Off — the format decides."}</p>
          )}
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Discovery</label>
          </div>
          <Select value={discoveryMode} onValueChange={(v) => set({ discoveryMode: v, discovery_mode: undefined, seo: undefined })}>
            <SelectTrigger className={triggerCls} style={triggerStyle} data-testid="recipe-discovery"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DISCOVERY_OPTIONS.map((option) => (
                <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] leading-snug text-muted-foreground">{discoveryOption.hint}</p>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Hashtags</label>
          <Select value={hashtagMode} onValueChange={(v) => set({ hashtagMode: v, hashtag_mode: undefined })}>
            <SelectTrigger className={triggerCls} style={triggerStyle} data-testid="recipe-hashtags"><SelectValue /></SelectTrigger>
            <SelectContent>
              {HASHTAG_OPTIONS.map((option) => (
                <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] leading-snug text-muted-foreground">{hashtagOption.hint}</p>
        </div>
        <div className="space-y-1 sm:col-span-2 lg:col-span-4">
          <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Destination link <span className="lowercase tracking-normal text-muted-foreground/70">(optional)</span></label>
          <Input
            value={value.destinationUrl || ""}
            onChange={(e) => set({ destinationUrl: e.target.value })}
            placeholder="https://plumbers.adamdoesai.com — where this campaign sends people"
            className={triggerCls}
            style={triggerStyle}
            data-testid="recipe-destination"
          />
          <p className="text-[11px] text-muted-foreground">Used as the post's link, overriding the brand's default site. Leave blank to use the brand website.</p>
        </div>
      </div>

      {/* Live preview of what the chosen voice + style actually do */}
      <div className="sm:col-span-3 space-y-2 rounded-lg border p-3" style={{ borderColor: variant === "dark" ? "rgba(255,255,255,0.12)" : "var(--border-subtle)", backgroundColor: variant === "dark" ? "rgba(255,255,255,0.05)" : "var(--surface-1)" }} data-testid="recipe-preview">
        <div className="flex gap-2">
          <span className="mt-0.5 w-14 shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Voice</span>
          <span className="text-[11.5px] leading-relaxed text-foreground/85">
            {selectedVoice
              ? <><span className="font-medium text-1">{selectedVoice.name}</span>{selectedVoice.instructions ? ` — ${selectedVoice.instructions.slice(0, 180)}${selectedVoice.instructions.length > 180 ? "…" : ""}` : " (no instructions yet — add them in Brands)"}</>
              : "No voice — add one in Brands."}
          </span>
        </div>
        <div className="flex gap-2">
          <span className="mt-0.5 w-14 shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Format</span>
          <span className="text-[11.5px] leading-relaxed text-foreground/85">
            {selectedStyle
              ? <><span className="font-medium text-1">{selectedStyle.name}</span>{selectedStyle.description ? ` — ${selectedStyle.description}` : ""}</>
              : "Agent picks the best-fit shape for your topic."}
          </span>
        </div>
        <div className="flex gap-2">
          <span className="mt-0.5 w-14 shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Auto</span>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Sparkles className="h-3 w-3" /> {brand?.industry ? `${brand.industry} ` : ""}industry knowledge + a critique polish pass are applied automatically.
          </span>
        </div>
      </div>
    </div>
  );
}
